// Poursuite du rat — fonctions pures, sans DOM.
import { fadePrints } from './trail.js';
// Le fromage est posé au milieu du chemin optimal et ne bouge pas. Le rat part du bout du chemin
// et avance d'une case par tic jusqu'au fromage, puis le mange pendant autant de tics qu'il lui en
// a fallu pour l'atteindre. La souris doit atteindre le fromage avant qu'il n'en reste plus rien.

/**
 * État initial d'une poursuite sur un chemin de `pathLength` cases (départ et arrivée inclus).
 * @returns {{ratIndex:number, cheeseIndex:number, travelSteps:number, cheeseLeft:number, fullGain:number}}
 *   ratIndex    : position du rat sur le chemin (index dans optimalPath)
 *   cheeseIndex : position fixe du fromage, au milieu du chemin
 *   travelSteps : nombre de tics du rat pour atteindre le fromage (= durée pour le manger)
 *   cheeseLeft  : fromage restant, en tics, de travelSteps (entier) à 0 (plus rien)
 *   fullGain    : points gagnés si la souris arrive avant le rat
 */
export function createChase(pathLength) {
  const last = Math.max(0, pathLength - 1);
  const cheeseIndex = Math.floor(last / 2);
  const travelSteps = last - cheeseIndex;
  return { ratIndex: last, cheeseIndex, travelSteps, cheeseLeft: travelSteps, fullGain: last };
}

export function hasRatReachedCheese(chase) {
  return chase.ratIndex <= chase.cheeseIndex;
}

/** Le rat a parcouru la moitié de son trajet jusqu'au fromage (sans y avoir encore mordu). */
export function hasRatReachedHalfway(chase) {
  return chase.ratIndex <= chase.cheeseIndex + Math.floor(chase.travelSteps / 2);
}

export function isCheeseGone(chase) {
  return chase.cheeseLeft <= 0;
}

/** Un tic : le rat avance d'une case, ou mange une part du fromage s'il y est déjà. */
export function advanceChase(chase) {
  if (!hasRatReachedCheese(chase)) return { ...chase, ratIndex: chase.ratIndex - 1 };
  if (isCheeseGone(chase)) return chase;
  return { ...chase, cheeseLeft: chase.cheeseLeft - 1 };
}

/** Part du fromage restante, de 1 (entier) à 0. */
export function cheeseRatio(chase) {
  return chase.travelSteps > 0 ? chase.cheeseLeft / chase.travelSteps : 0;
}

/** Points en jeu : tous tant que le rat n'a pas mordu, puis proportionnels au fromage restant. */
export function chaseGain(chase) {
  return Math.round(chase.fullGain * cheeseRatio(chase));
}

/**
 * Verdict quand la souris atteint le fromage (ou quand il n'en reste plus).
 * @returns {'win'|'survive'|'lose'} win : avant le rat (niveau suivant) ; survive : fromage
 *   entamé, points partiels mais niveau à rejouer ; lose : plus de fromage.
 */
export function chaseOutcome(chase) {
  if (isCheeseGone(chase)) return 'lose';
  if (!hasRatReachedCheese(chase)) return 'win';
  return 'survive';
}

/**
 * Traces de pattes laissées par le rat, du bout du chemin jusqu'à sa position : deux empreintes
 * par case parcourue, alternativement à gauche et à droite de sa trajectoire, orientées dans son
 * sens de marche (coordonnées en cases, centre d'une case = x + 0.5).
 * Les empreintes trop proches du rat (moins de `clearance` cases de `ratPos`, son centre affiché)
 * sont omises : elles seraient sous son corps ou collées à lui.
 * @param {{x:number,y:number}[]} path chemin optimal
 * @param {{ ratPos?: {x:number,y:number}, clearance?: number }} [options] ratPos : case (même
 *   repère que path) où le rat est dessiné, par défaut celle de ratIndex
 * @returns {{x:number, y:number, angle:number, age:number}[]} de la plus ancienne à la plus
 *   récente ; age de 1 (première empreinte) à 0 (la dernière, près du rat)
 */
export function ratPawPrints(path, ratIndex, { ratPos = path[ratIndex], clearance = 0 } = {}) {
  const prints = [];
  for (let i = path.length - 1; i > ratIndex; i--) {
    const from = path[i];
    const to = path[i - 1];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    for (const t of [0.25, 0.75]) {
      const side = prints.length % 2 === 0 ? -1 : 1;
      prints.push({
        // Décalage perpendiculaire à la marche (−dy, dx) : pattes gauche et droite
        x: from.x + 0.5 + dx * t - dy * side * 0.1,
        y: from.y + 0.5 + dy * t + dx * side * 0.1,
        angle: Math.atan2(dy, dx),
      });
    }
  }
  return fadePrints(prints, ratPos && { x: ratPos.x + 0.5, y: ratPos.y + 0.5 }, clearance);
}
